/**
 * Helpers for hiding appointment dates and time slots that are already in the
 * past. "Past" is measured on the wall clock of the timezone picked in the
 * form, not the browser's own -- shared by the create and reschedule modals.
 */

/**
 * The UTC offset, in minutes, of a timezone, read off the `timezones` payload
 * the modals load (a current timestamp per zone ending in e.g. "+08:00").
 * Falls back to the browser's own offset when the zone is unknown or the
 * payload hasn't loaded yet.
 * @param {Object|null} timezones - { [zoneName]: ISO timestamp with offset }
 * @param {string} timezone - the selected zone name, e.g. "Asia/Kuala_Lumpur"
 * @returns {number} minutes east of UTC
 */
export const offsetMinutesFor = (timezones, timezone) => {
  const timestamp = timezones?.[timezone];
  const match = timestamp?.match(/([+-])(\d{2}):(\d{2})$/);
  if (!match) return -new Date().getTimezoneOffset();
  const sign = match[1] === '-' ? -1 : 1;
  return sign * (Number(match[2]) * 60 + Number(match[3]));
};

/**
 * The current instant shifted onto a timezone's wall clock. Read it back with
 * the UTC getters (getUTCHours, getUTCMonth, ...) -- the shifted Date's UTC
 * fields are the wall-clock fields in that timezone.
 * @param {number} offsetMinutes - minutes east of UTC
 * @param {number} [nowMs] - the instant to use, for tests; defaults to now
 * @returns {Date}
 */
export const shiftedNow = (offsetMinutes, nowMs = Date.now()) =>
  new Date(nowMs + offsetMinutes * 60000);

/**
 * Today's date and the current time on a timezone's wall clock.
 * @param {number} offsetMinutes - minutes east of UTC
 * @param {number} [nowMs] - the instant to use, for tests; defaults to now
 * @returns {{ today: string, nowHm: string }} "YYYY-MM-DD" and "HH:MM"
 */
export const todayAndNow = (offsetMinutes, nowMs = Date.now()) => {
  const now = shiftedNow(offsetMinutes, nowMs);
  const yyyy = now.getUTCFullYear();
  const mm = String(now.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(now.getUTCDate()).padStart(2, '0');
  const hh = String(now.getUTCHours()).padStart(2, '0');
  const min = String(now.getUTCMinutes()).padStart(2, '0');
  return { today: `${yyyy}-${mm}-${dd}`, nowHm: `${hh}:${min}` };
};

/**
 * Drops dates earlier than today. Dates are "YYYY-MM-DD", so a plain string
 * comparison orders them correctly.
 * @param {string[]|null|undefined} dates
 * @param {string} today - "YYYY-MM-DD"
 * @returns {string[]}
 */
export const filterPastDates = (dates, today) =>
  (dates || []).filter(date => date >= today);

/**
 * Drops slots that have already started when the picked date is today. Slots
 * on any other date are returned untouched.
 * @param {Object[]|null|undefined} slots
 * @param {string} date - the picked date, "YYYY-MM-DD"
 * @param {string} timeField - the slot's time property, e.g. "schedule_time"
 * @param {string} today - "YYYY-MM-DD"
 * @param {string} nowHm - "HH:MM"
 * @returns {Object[]}
 */
export const filterPastTimeSlots = (slots, date, timeField, today, nowHm) => {
  if (!slots) return [];
  if (date !== today) return slots;
  // v1 slots are "HH:MM" (no seconds) and v2 ones "HH:MM:SS". Comparing an
  // "HH:MM" slot against a "HH:MM:SS" now would make an on-the-hour slot always
  // look earlier than now (string prefix rule), so compare both at HH:MM
  // precision. A slot at exactly the current minute counts as past.
  return slots.filter(slot => slot[timeField].slice(0, 5) > nowHm);
};
